import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Building2,
  ChevronDown,
  Package,
  Settings,
  Ticket,
  Users,
} from "lucide-react";

import { useMe } from "@/hooks/useMe";
import {
  ROUTE_OPERATORS,
  ROUTE_SERVICE_REQUESTS,
  ROUTE_SYSTEM_CONFIG,
  ROUTE_TENANTS,
  ROUTE_THEMES,
} from "@/constants/routes";
import type { MeResponse } from "@/types/me";

import "./HomePage.css";

/* ── Accordion panel ────────────────────────────────────────── */

interface AccordionPanelProps {
  readonly id: string;
  readonly icon: ReactNode;
  readonly title: string;
  readonly viewAllLink: string;
  readonly viewAllLabel?: string;
  readonly count?: number;
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
}

/**
 * Purely presentational accordion. No data fetching. No async loading.
 * Admin sections have static copy for CR-92; future CRs will swap the
 * static card bodies for live data as backend endpoints land.
 */
function AccordionPanel({
  id,
  icon,
  title,
  viewAllLink,
  viewAllLabel,
  count,
  defaultOpen = false,
  children,
}: AccordionPanelProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      data-section-id={id}
      className={`hub-accordion__panel ${open ? "hub-accordion__panel--open" : ""}`}
    >
      <button
        id={`hub-accordion-${id}`}
        type="button"
        className="hub-accordion__trigger"
        onClick={(): void => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={`hub-accordion-body-${id}`}
      >
        <span className="hub-accordion__trigger-left">
          <span className="hub-accordion__icon">{icon}</span>
          <span className="hub-accordion__title">{title}</span>
          {typeof count === "number" && (
            <span className="hub-accordion__count">{count}</span>
          )}
        </span>
        <ChevronDown size={18} className="hub-accordion__chevron" />
      </button>

      <div
        id={`hub-accordion-body-${id}`}
        role="region"
        aria-labelledby={`hub-accordion-${id}`}
        className="hub-accordion__body"
      >
        <div className="hub-accordion__items">{children}</div>
        <div className="hub-accordion__footer">
          <Link to={viewAllLink} className="hub-accordion__view-all">
            View all {viewAllLabel ?? title} <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── Placeholder card ────────────────────────────────────────── */

function PlaceholderCard({ text }: { readonly text: string }): JSX.Element {
  return (
    <div className="hub-mini-card hub-mini-card--placeholder">
      <div className="hub-mini-card__body">
        <p className="hub-mini-card__desc">{text}</p>
      </div>
    </div>
  );
}

/* ── Name derivation ─────────────────────────────────────────── */

/**
 * Derive the operator's first name for the welcome banner.
 *
 * Primary: first whitespace-split token of `display_name`.
 * Fallback: email local part (everything before `@`).
 * Last resort: empty string — caller falls back to the generic hero.
 */
export function deriveFirstName(me: MeResponse | null): string {
  if (!me) return "";
  const fromDisplay = me.display_name.trim().split(/\s+/)[0] ?? "";
  if (fromDisplay) return fromDisplay;
  const local = me.email.split("@")[0] ?? "";
  return local;
}

/* ── Main page ───────────────────────────────────────────────── */

export function HomePage(): JSX.Element {
  const { me } = useMe();
  const firstName = deriveFirstName(me);

  return (
    <>
      <Helmet>
        <title>Home — Soft9Tech Admin</title>
        <meta
          name="description"
          content="Soft9Tech Admin — your workspace for tenants, themes, service requests, and operations."
        />
      </Helmet>

      <div className="my-hub-page">
        <div className="my-hub-page__welcome">
          <h1 className="my-hub-page__heading">
            {firstName
              ? `Hi ${firstName}, welcome to Soft9Tech Admin`
              : "Welcome to Soft9Tech Admin"}
          </h1>
          <p className="my-hub-page__subheading">
            Your workspace for tenants, themes, service requests, and
            operations.
          </p>
        </div>

        <div className="hub-accordion">
          <AccordionPanel
            id="tenants"
            icon={<Building2 size={16} />}
            title="Tenants"
            viewAllLink={ROUTE_TENANTS}
            count={0}
            defaultOpen
          >
            <PlaceholderCard text="No tenants yet — coming soon." />
          </AccordionPanel>

          <AccordionPanel
            id="themes"
            icon={<Package size={16} />}
            title="Themes"
            viewAllLink={ROUTE_THEMES}
            count={0}
          >
            <PlaceholderCard text="Theme catalog — browse and manage." />
          </AccordionPanel>

          <AccordionPanel
            id="service-requests"
            icon={<Ticket size={16} />}
            title="Service Requests"
            viewAllLink={ROUTE_SERVICE_REQUESTS}
            count={0}
          >
            <PlaceholderCard text="No service requests — coming soon." />
          </AccordionPanel>

          <AccordionPanel
            id="operators"
            icon={<Users size={16} />}
            title="Operators"
            viewAllLink={ROUTE_OPERATORS}
            count={0}
          >
            <PlaceholderCard text="Operators — coming soon." />
          </AccordionPanel>

          <AccordionPanel
            id="system-config"
            icon={<Settings size={16} />}
            title="System Config"
            viewAllLink={ROUTE_SYSTEM_CONFIG}
            viewAllLabel="settings"
          >
            <PlaceholderCard text="Configuration panel coming soon." />
          </AccordionPanel>
        </div>
      </div>
    </>
  );
}
